import { CustomerUser } from '../types/customerAuth';

const FACE_LIVENESS_TYPES = [
  'FACE_LIVENESS_RIGHT',
  'FACE_LIVENESS_LEFT',
  'FACE_LIVENESS_STRAIGHT',
] as const;

export type RegisterFlowStage =
  | 'account'
  | 'verify-email'
  | 'business-profile'
  | 'business-submitted'
  | 'face-liveness'
  | 'phone-verification'
  | 'completed';

function hasFaceLivenessPack(user: CustomerUser): boolean {
  const attachments = user.attachments ?? {};
  const values = Object.values(attachments);

  return (
    values.some((attachment) =>
      FACE_LIVENESS_TYPES.includes(String(attachment?.fileType ?? '') as (typeof FACE_LIVENESS_TYPES)[number]),
    ) ||
    Object.keys(attachments).some((key) =>
      FACE_LIVENESS_TYPES.includes(key as (typeof FACE_LIVENESS_TYPES)[number]),
    )
  );
}

export function nextStageForCustomer(user: CustomerUser | null): RegisterFlowStage {
  if (!user) return 'account';

  const step = user.onboardingsStep;
  const accountType = user.accountType === 'BUSINESS' ? 'BUSINESS' : 'INDIVIDUAL';
  const businessVerificationStatus = String(user.businessVerificationStatus ?? '').toUpperCase();

  if (user.emailVerified === false) {
    return 'verify-email';
  }

  if (accountType === 'BUSINESS') {
    if (businessVerificationStatus === 'APPROVED' || step === 'COMPLETED') {
      return 'completed';
    }

    if (businessVerificationStatus === 'SUBMITTED') {
      return 'business-submitted';
    }

    return 'business-profile';
  }

  if (step === 'ACCOUNT_CREATED') {
    return user.emailVerified ? 'face-liveness' : 'verify-email';
  }

  if (step === 'EMAIL_VERIFIED') {
    return 'face-liveness';
  }

  if (step === 'FACE_LIVENESS_CHALLENGE') {
    return user.phoneVerified ? 'completed' : 'phone-verification';
  }

  if (step === 'COMPLETED') {
    return 'completed';
  }

  if (!hasFaceLivenessPack(user)) {
    return 'face-liveness';
  }

  if (user.phoneVerified === false) {
    return 'phone-verification';
  }

  return 'completed';
}

export function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}
